import { useTranslation } from "react-i18next";
import { GoMakeDialog, GomakeOutlinedTextInput, LazyRenderList } from "@/components";
import { Stack, CircularProgress, Box, Button, IconButton } from "@mui/material";
import { useStyle } from "@/pages-components/board-missions/style";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDeliveryTickerPdfApi, printDeliveryTickerPdfApi, checkIfProductIsLabelInRollsApi } from "@/services/api-service/board-missions/board-missions-table";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { useIsMobile } from "@/hooks/use-responsive";
import { downloadPdf, generateFilename } from "@/utils/helpers";
import { ThreeOptionsModal } from "@/components/modal-deleted/three-options-modal";
import { ModalType } from "@/enums";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { EPreferences } from "@/enums/prefernces";
import {
  PackingSlipPackage,
  buildNestedPackagesPayload,
  buildPackagesPayload,
  createPackages,
  isDeliveryTicketSize150x100V2,
  isPackagesDistributionValid,
  redistributePackageRows,
  updatePackageQuantityKeepingTotal,
  updateRowFromRollsAndStickers,
  updateRowQuantityKeepingPackageTotal,
} from "./print-packing-slip-distribution";
import { printHouseHasPackagingSlipPrinter } from "@/store/print-house-profile";
import { useRecoilValue } from "recoil";

interface IProps {
  boardMissionId: string;
  quantity: number;
  openModal: boolean;
  onClose: () => void;
}

const PrintPackingSlipModalWidget = ({
  boardMissionId,
  quantity,
  openModal,
  onClose,
}: IProps) => {
  const isMobile = useIsMobile();
  const { classes } = useStyle();
  const { t } = useTranslation();
  const { alertFault, alertSuccess } = useSnackBar();
  const { callApi } = useGomakeAxios();
  const { errorColor } = useGomakeTheme();
  const { GetPreferenceValueKey, CheckPreferenceValue } = usePrintHousePreferences();

  const deliveryTicketSize = useMemo(() => {
    const resolvedSize = GetPreferenceValueKey(EPreferences.DELIVERY_TICKET_SIZE);
    if (resolvedSize) {
      return resolvedSize;
    }
    const rawSize = CheckPreferenceValue(EPreferences.DELIVERY_TICKET_SIZE);
    return typeof rawSize === "string" ? rawSize : undefined;
  }, [GetPreferenceValueKey, CheckPreferenceValue]);

  const useNestedPackagePayload = isDeliveryTicketSize150x100V2(deliveryTicketSize);
  const [openPrintExportModal, setOpenPrintExportModal] = useState<boolean>(false);
  const [totalQuantity, setTotalQuantity] = useState<number>(quantity);
  const [quantityOfPackages, setQuantityOfPackages] = useState<number>(1);
  const [quantityPerPackage, setQuantityPerPackage] =
    useState<number>(quantity);
  const [isCheckingLabelInRolls, setIsCheckingLabelInRolls] = useState<boolean>(false);
  const [isLabelInRolls, setIsLabelInRolls] = useState<boolean>(false);
  const [packages, setPackages] = useState<PackingSlipPackage[]>(() =>
    createPackages(quantity || 0, 1),
  );

  const getSafePackagesCount = (count: number, total: number): number => {
    if (!Number.isFinite(count) || count <= 0) {
      return 0;
    }
    const normalizedCount = Math.floor(count);
    if (total > 0) {
      return Math.min(normalizedCount, total);
    }
    return normalizedCount;
  };

  const applyPackageDistribution = (
    nextTotal: number,
    nextPackagesCount: number,
    options?: {
      previousPackages?: PackingSlipPackage[];
      preserveRowCounts?: boolean;
      baseQuantity?: number;
    },
  ) => {
    const nextPackages = createPackages(nextTotal, nextPackagesCount, options);
    setPackages(nextPackages);
    setQuantityPerPackage(nextPackages[0]?.packageQuantity || 0);
  };

  useEffect(() => {
    if (openModal) {
      const initialTotal = quantity || 0;
      setTotalQuantity(initialTotal);
      setQuantityOfPackages(1);
      setQuantityPerPackage(initialTotal);
      setIsLabelInRolls(false);
      setPackages(createPackages(initialTotal, 1));
    }
  }, [openModal, quantity]);

  useEffect(() => {
    if (openModal && boardMissionId) {
      setIsCheckingLabelInRolls(true);
      const callBack = (res) => {
        setIsCheckingLabelInRolls(false);
        if (res?.success) {
          setIsLabelInRolls(res.data?.isLabelInRolls || false);
        } else {
          setIsLabelInRolls(false);
        }
      };
      checkIfProductIsLabelInRollsApi(callApi, callBack, boardMissionId);
    } else {
      setIsCheckingLabelInRolls(false);
      setIsLabelInRolls(false);
    }
  }, [openModal, boardMissionId, callApi]);

  const handleTotalQuantityChange = (event) => {
    const newTotalQuantity = parseInt(event.target.value, 10);

    if (isNaN(newTotalQuantity) || newTotalQuantity <= 0) {
      return;
    }

    const nextPackagesCount = getSafePackagesCount(quantityOfPackages, newTotalQuantity);
    if (nextPackagesCount <= 0) {
      return;
    }

    setTotalQuantity(newTotalQuantity);
    setQuantityOfPackages(nextPackagesCount);
    applyPackageDistribution(newTotalQuantity, nextPackagesCount, {
      previousPackages: packages,
      preserveRowCounts: true,
    });
  };

  const handleQuantityOfPackagesChange = (event) => {
    const parsedCount = parseInt(event.target.value, 10);

    if (isNaN(parsedCount) || parsedCount <= 0) {
      return;
    }

    const nextPackagesCount = getSafePackagesCount(parsedCount, totalQuantity);
    if (nextPackagesCount <= 0) {
      return;
    }

    setQuantityOfPackages(nextPackagesCount);
    applyPackageDistribution(totalQuantity, nextPackagesCount);
  };

  const handleQuantityPerPackageChange = (event) => {
    const newQuantityPerPackage = parseInt(event.target.value, 10);

    if (isNaN(newQuantityPerPackage) || newQuantityPerPackage <= 0) {
      return;
    }

    const nextPackagesCount = getSafePackagesCount(
      Math.ceil(totalQuantity / newQuantityPerPackage),
      totalQuantity,
    );
    if (nextPackagesCount <= 0) {
      return;
    }

    setQuantityOfPackages(nextPackagesCount);
    applyPackageDistribution(totalQuantity, nextPackagesCount, {
      baseQuantity: newQuantityPerPackage,
    });
  };

  const handlePackageQuantityChange = (index: number, newValue: number) => {
    if (isNaN(newValue) || newValue <= 0) {
      return;
    }

    setPackages((prevPackages) => {
      const updatedPackages = updatePackageQuantityKeepingTotal(
        prevPackages,
        index,
        newValue,
        totalQuantity,
      );
      return updatedPackages || prevPackages;
    });
  };

  const handleRowQuantityChange = (
    packageIndex: number,
    rowIndex: number,
    newValue: number,
  ) => {
    if (isNaN(newValue) || newValue < 0) {
      return;
    }

    setPackages((prevPackages) => {
      const currentPackage = prevPackages[packageIndex];
      if (!currentPackage) {
        return prevPackages;
      }

      const updatedPackage = updateRowQuantityKeepingPackageTotal(
        currentPackage,
        rowIndex,
        newValue,
      );
      if (!updatedPackage) {
        return prevPackages;
      }

      return prevPackages.map((packingPackage, index) =>
        index === packageIndex ? updatedPackage : packingPackage,
      );
    });
  };

  const handleRowFieldChange = (
    packageIndex: number,
    rowIndex: number,
    field: "rollsAmount" | "stickersPerRoll",
    value: number,
  ) => {
    if (isNaN(value) || value < 0) {
      return;
    }

    setPackages((prevPackages) => {
      const currentPackage = prevPackages[packageIndex];
      if (!currentPackage) {
        return prevPackages;
      }

      const updatedPackage = updateRowFromRollsAndStickers(
        currentPackage,
        rowIndex,
        field,
        value,
      );
      if (!updatedPackage) {
        return prevPackages;
      }

      return prevPackages.map((packingPackage, index) =>
        index === packageIndex ? updatedPackage : packingPackage,
      );
    });
  };

  const handleAddRow = (packageIndex: number) => {
    setPackages((prevPackages) =>
      prevPackages.map((packingPackage, index) => {
        if (index !== packageIndex) {
          return packingPackage;
        }

        const nextRowsCount = packingPackage.rows.length + 1;
        if (
          packingPackage.packageQuantity > 0 &&
          nextRowsCount > packingPackage.packageQuantity
        ) {
          return packingPackage;
        }

        return redistributePackageRows(packingPackage, nextRowsCount);
      }),
    );
  };

  const handleRemoveRow = (packageIndex: number, rowIndex: number) => {
    setPackages((prevPackages) =>
      prevPackages.map((packingPackage, index) => {
        if (index !== packageIndex || packingPackage.rows.length <= 1) {
          return packingPackage;
        }

        const remainingCount = packingPackage.rows.length - 1;
        if (rowIndex < 0 || rowIndex >= packingPackage.rows.length) {
          return packingPackage;
        }

        return redistributePackageRows(packingPackage, remainingCount);
      }),
    );
  };

  const isDistributionValid = useMemo(
    () =>
      isPackagesDistributionValid(totalQuantity, packages, isLabelInRolls) &&
      quantityPerPackage <= totalQuantity,
    [totalQuantity, packages, isLabelInRolls, quantityPerPackage],
  );

  const getPackagesRequestPayload = () =>
    useNestedPackagePayload
      ? buildNestedPackagesPayload(packages, isLabelInRolls)
      : buildPackagesPayload(packages, isLabelInRolls);

  const renderPackageInput = useCallback(
    (index: number) => {
      const packingPackage = packages[index];
      if (!packingPackage) {
        return null;
      }

      const rowCount = packingPackage.rows.length;

      return (
        <div key={index} style={classes.packageCardStyle}>
          <div style={classes.packageCardHeaderStyle}>
            <h3 style={{ ...classes.packageLabelStyle, marginBottom: 0 }}>
              {`${t("boardMissions.package")} ${index + 1}`}
            </h3>
            {isLabelInRolls && (
              <span style={classes.packageRowBadgeStyle}>
                {rowCount === 1
                  ? t("boardMissions.packageRowCount", { count: rowCount })
                  : t("boardMissions.packageRowCountPlural", { count: rowCount })}
              </span>
            )}
          </div>
          <div style={{ width: isLabelInRolls ? "33%" : "100%", marginBottom: isLabelInRolls ? 18 : 0 }}>
            <GomakeOutlinedTextInput
              label={t("boardMissions.packageQuantity")}
              type="number"
              inputHeight={40}
              value={packingPackage.packageQuantity || 0}
              onChange={(e) => {
                const newValue = parseInt(e.target.value, 10);
                handlePackageQuantityChange(index, newValue);
              }}
              disabled={isCheckingLabelInRolls || packages.length === 1}
            />
          </div>
          {isLabelInRolls && (
            <Stack direction="column" gap={"10px"}>
              {packingPackage.rows.map((row, rowIndex) => (
                <Stack
                  key={`${index}-${rowIndex}`}
                  direction="row"
                  gap={"10px"}
                  alignItems="center"
                >
                  <div style={{ width: "33%", flex: 1 }}>
                    <GomakeOutlinedTextInput
                      label={t("boardMissions.quantity")}
                      type="number"
                      inputHeight={40}
                      value={row.quantity || 0}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        handleRowQuantityChange(index, rowIndex, newValue);
                      }}
                      disabled={isCheckingLabelInRolls}
                    />
                  </div>
                  <div style={{ width: "33%", flex: 1 }}>
                    <GomakeOutlinedTextInput
                      label={t("boardMissions.rollsAmount")}
                      type="number"
                      inputHeight={40}
                      value={row.rollsAmount || 0}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        handleRowFieldChange(
                          index,
                          rowIndex,
                          "rollsAmount",
                          newValue,
                        );
                      }}
                      disabled={isCheckingLabelInRolls}
                    />
                  </div>
                  <div style={{ width: "33%", flex: 1 }}>
                    <GomakeOutlinedTextInput
                      label={t("boardMissions.stickersPerRoll")}
                      type="number"
                      inputHeight={40}
                      value={row.stickersPerRoll || 0}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        handleRowFieldChange(
                          index,
                          rowIndex,
                          "stickersPerRoll",
                          newValue,
                        );
                      }}
                      disabled={isCheckingLabelInRolls}
                    />
                  </div>
                  {rowCount > 1 && (
                    <IconButton
                      aria-label={t("boardMissions.removePackageRow")}
                      onClick={() => handleRemoveRow(index, rowIndex)}
                      disabled={isCheckingLabelInRolls}
                      sx={{
                        marginInlineStart: "2px",
                      }}
                    >
                      <DeleteOutlineIcon
                        sx={{ fontSize: 18, color: errorColor(500) }}
                      />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button
                variant="text"
                onClick={() => handleAddRow(index)}
                disabled={
                  isCheckingLabelInRolls ||
                  (packingPackage.packageQuantity > 0 &&
                    packingPackage.rows.length >= packingPackage.packageQuantity)
                }
                style={classes.addPackageRowButtonStyle}
              >
                {`+ ${t("boardMissions.addPackageRow")}`}
              </Button>
            </Stack>
          )}
        </div>
      );
    },
    [
      isLabelInRolls,
      packages,
      isCheckingLabelInRolls,
      classes,
      t,
      errorColor,
    ],
  );

  const onClickPrintPackagingSlip = async () => {
    if (!isDistributionValid) {
      alertFault("boardMissions.invalidPackageDistribution");
      return;
    }

    if (quantityPerPackage > totalQuantity) {
      alertFault("boardMissions.alertQuantity");
      return;
    }

    //setOpenPrintExportModal(true);
    exportPdf();
  };

  const exportPdf = async () => {
    onClose();
    const callBack = (res) => {
      if (res?.success) {
        const pdfLink = res.data;
        const filename = generateFilename("Packing_Slip", boardMissionId, undefined, "pdf");
        downloadPdf(pdfLink, filename);
      } else {
        alertFault("boardMissions.alertPrintPackingSlip");
      }
    };

    await getDeliveryTickerPdfApi(callApi, callBack, {
      boardMissionId: boardMissionId,
      packages: getPackagesRequestPayload(),
    });
  };

  const printPackingSlip = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccess("boardMissions.packingSlipPrinted");
        onClose();
      } else {
        alertFault("boardMissions.alertPrintPackingSlip");
      }
    };

    await printDeliveryTickerPdfApi(callApi, callBack, {
      boardMissionId: boardMissionId,
      packages: getPackagesRequestPayload(),
    });
  };

  return (
    <>
      <GoMakeDialog
        open={openModal}
        onClose={onClose}
        title={t("boardMissions.packagesDistributionTitle")}
        primaryButtonLabel={t("modal.confirm")}
        cancelButtonLabel={t("modal.cancel")}
        onPrimaryAction={onClickPrintPackagingSlip}
        onCancel={onClose}
        primaryButtonDisabled={isCheckingLabelInRolls || !isDistributionValid}
        cancelButtonDisabled={isCheckingLabelInRolls}
        fullWidth
        sx={{ zIndex: 999999 }}
        customStyles={{
          paper: {
            maxWidth: isMobile ? "90vw" : 650,
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          {isCheckingLabelInRolls && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <CircularProgress size={24} />
                <span style={{ fontSize: "0.875rem", color: "#666" }}>
                  {t("Loading")}...
                </span>
              </Stack>
            </Box>
          )}
          <Stack direction={"column"} gap={"15px"}>
            <span style={{ ...classes.subTitle, width: "100%", }}>
              {t("boardMissions.packagesDistributionSubtitle")}
            </span>
            <Stack direction={"column"} gap={"15px"}>
              <Stack direction={"row"} gap={"15px"}>
                <div style={{ width: "33%" }}>
                  <GomakeOutlinedTextInput
                    label={t("boardMissions.totalQuantity")}
                    value={totalQuantity}
                    onChange={handleTotalQuantityChange}
                    type="number"
                    inputHeight={40}
                    disabled={isCheckingLabelInRolls}
                  />
                </div>
                <div style={{ width: "33%" }}>
                  <GomakeOutlinedTextInput
                    label={t("boardMissions.quantityOfPackages")}
                    value={quantityOfPackages}
                    onChange={handleQuantityOfPackagesChange}
                    type="number"
                    inputHeight={40}
                    disabled={isCheckingLabelInRolls}
                  />
                </div>
                <div style={{ width: "33%" }}>
                  <GomakeOutlinedTextInput
                    label={t("boardMissions.quantityPerPackage")}
                    value={quantityPerPackage}
                    onChange={handleQuantityPerPackageChange}
                    type="number"
                    inputHeight={40}
                    disabled={isCheckingLabelInRolls}
                  />
                </div>
              </Stack>

              <LazyRenderList
                itemCount={packages.length}
                renderItem={renderPackageInput}
                batchSize={60}
                maxHeight={400}
              />
            </Stack>
            <span style={classes.packagesSummaryStyle}>
              {t("boardMissions.packagesSummary", {
                count: packages.length,
                quantity: totalQuantity,
              })}
            </span>
          </Stack>
        </Box>
      </GoMakeDialog>

      {(
        <ThreeOptionsModal
          modalType={ModalType.WARNING}
          title="boardMissions.selectPackingSlipAction"
          subTitle="boardMissions.selectPackingSlipActionSubtitle"
          yesBtn="boardMissions.exportPdf"
          noBtn="boardMissions.print"
          openModal={openPrintExportModal}
          onClose={() => setOpenPrintExportModal(false)}
          onClickYes={() => {
            setOpenPrintExportModal(false);
            exportPdf();
          }}
          onClickNo={() => {
            setOpenPrintExportModal(false);
            printPackingSlip();
          }}
          style={
            isMobile
              ? {
                width: "95vw",
                maxWidth: 600,
                minWidth: "unset",
              }
              : undefined
          }
        />
      )}
    </>
  );
};

export { PrintPackingSlipModalWidget };
